import React from "react";
import { Link, Navigate } from "react-router-dom";
import RelatedClaimHelp from "./RelatedClaimHelp";
import ContentSections from "./ContentSections";
import ReviewedBy from "./ReviewedBy";
import {
  Shield,
  Scale,
  FileText,
  TrendingUp,
  MapPin,
  Phone,
  ArrowRight,
  Star,
  Clock,
  Gavel,
  Calculator,
} from "lucide-react";
import Header from "./Header";
import Footer from "./Footer";
import Breadcrumbs from "./Breadcrumbs";
import SEO from "./SEO";
import CalculatorCallout from "./CalculatorCallout";
import { businessInfo, images } from "../mock";
import { flagshipCities } from "../data/flagshipCities";
import {
  localBusinessSchema,
  faqSchema,
  breadcrumbSchema,
} from "../utils/schemas";

const statutes = [
  {
    ref: "Fla. Stat. § 627.7142",
    title: "Homeowner Claims Bill of Rights",
    text: "Florida law guarantees homeowners specific rights during a claim — including prompt acknowledgment, a coverage decision within statutory timeframes, and the right to hire your own representation. A public adjuster works entirely for you under these protections, never the carrier.",
  },
  {
    ref: "Fla. Stat. § 627.70132",
    title: "Claim Filing Deadlines",
    text: "You generally have 1 year from the date of loss to file a new property insurance claim and 18 months to file a supplemental or reopened claim for hurricane and windstorm losses. Missing these windows can permanently bar recovery — which is why fast documentation matters.",
  },
  {
    ref: "Fla. Stat. § 627.7015",
    title: "Mediation of Residential Claims",
    text: "Florida provides a state-administered mediation program for disputed residential property claims through the Department of Financial Services. It's a faster, lower-cost path than litigation, and carriers are required to participate in good faith.",
  },
  {
    ref: "Fla. Stat. § 626.854",
    title: "Public Adjuster Definition & Fee Caps",
    text: "This statute defines a public adjuster and caps fees to protect homeowners — generally 20% of the claim payment, and 10% for claims during the first year of a declared state of emergency. Fees come out of the recovery, so there's no upfront cost to you.",
  },
];

const buildFaqs = (c) => {
  const areas = c.neighborhoods.slice(0, 5).join(", ");
  return [
    {
      question: `Who is the best public adjuster in ${c.name}, FL?`,
      answer: `The best public adjuster for your ${c.name} claim is one who is Florida-licensed, works only for policyholders (never insurers), knows local ${c.region} carrier patterns and building codes, and charges nothing upfront. Claim Beast Public Adjusters is a ${c.region}-based, 5.0-rated firm (FL license ${businessInfo.licenses.florida}, firm ${businessInfo.licenses.firm}) that handles roof, wind, hurricane, water, hail, fire, denied, and underpaid claims across every ${c.name} neighborhood — with fees only paid out of what we recover for you.`,
    },
    {
      question: `What does a public adjuster do in ${c.name}?`,
      answer: `A public adjuster is a licensed professional who represents you — the homeowner — instead of the insurance company. In ${c.name} we inspect and document your damage, read your policy, build an independent Xactimate estimate at fair Florida market pricing, file and negotiate the claim, and escalate to appraisal or mediation when the carrier underpays. The goal is a full, correct payout instead of the carrier's opening number.`,
    },
    {
      question: `How much does a public adjuster cost in ${c.name}?`,
      answer:
        "Under Fla. Stat. § 626.854, public adjuster fees in Florida are capped — generally 20% of the amount recovered, and 10% for claims filed during the first year of a declared state of emergency. There is no upfront cost. The fee is a percentage of the additional money we recover, so in nearly every case you net significantly more than dealing with the carrier alone.",
    },
    {
      question: `Is it worth hiring a public adjuster for a ${c.name} claim?`,
      answer:
        "For most underpaid or denied claims, yes. Industry data consistently shows policyholders who use a public adjuster recover substantially more than those who don't. Because the fee only comes out of the additional recovery, the question is usually whether the carrier's offer is fair — and a free review answers that at no risk.",
    },
    {
      question: `How long do I have to file a claim in ${c.name}, FL?`,
      answer:
        "Under Fla. Stat. § 627.70132, you generally have 1 year from the date of loss to file a new hurricane or windstorm claim and 18 months to file a supplemental or reopened claim. Non-hurricane perils vary by policy. Act quickly — evidence degrades and deadlines are strict.",
    },
    {
      question: `Can you help with a denied or underpaid ${c.name} claim?`,
      answer:
        "Yes — that's the core of what we do. Most denials rely on beatable reasons (wear and tear, 'no functional damage,' misclassified perils). We rebuild the claim with objective evidence and file a formal dispute or supplemental. Florida allows reopening and supplementing claims within the statutory windows, plus appraisal and mediation.",
    },
    {
      question: `What areas of ${c.name} do you serve?`,
      answer: `We serve every ${c.name} neighborhood — including ${areas}, and beyond — plus the entire ${c.region} area. We're based in ${businessInfo.address.city}, FL and can typically be on-site for an inspection within 24–48 hours.`,
    },
    {
      question: "Does a public adjuster work with my contractor or attorney?",
      answer:
        "Yes. We coordinate with your roofer or restoration contractor to align scope and pricing, and we only involve attorneys when bad-faith litigation is genuinely warranted. Most claims resolve through documentation and negotiation without ever needing a lawyer.",
    },
    {
      question: "Do I have to accept my insurance company's estimate?",
      answer:
        "No. The carrier's estimate is an opening position, not a final decision. You have the right under the Homeowner Claims Bill of Rights (Fla. Stat. § 627.7142) to dispute it and to hire your own representation. Before you sign anything or cash a check with a release, get a free review.",
    },
    {
      question: `How do I get started with a free ${c.name} claim review?`,
      answer:
        "Call us or request a free review online. Send us your policy, any carrier estimate or denial letter, and photos of the damage. Within 48 hours we'll tell you whether you're being underpaid or wrongly denied and exactly what your claim should be worth — with no cost and no obligation.",
    },
  ];
};

const StatCard = ({ value, label }) => (
  <div className="text-center">
    <div
      className="text-3xl sm:text-4xl font-black text-[#E11D26] leading-none"
      style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
    >
      {value}
    </div>
    <div className="mt-2 text-white/60 text-xs uppercase tracking-wider font-bold">
      {label}
    </div>
  </div>
);

const H2 = ({ children }) => (
  <h2
    className="text-3xl sm:text-4xl font-black text-white uppercase leading-tight"
    style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
  >
    {children}
  </h2>
);

const FlagshipCityPage = ({ slug }) => {
  const c = flagshipCities[slug];
  if (!c) return <Navigate to="/" replace />;

  const faqs = buildFaqs(c);
  const schemas = [
    localBusinessSchema({
      areaServed: [`${c.name} FL`, `${c.region} FL`, `${c.county} FL`],
      geo: c.geo,
      city: c.slug,
    }),
    faqSchema(faqs),
    breadcrumbSchema([
      { name: "Home", path: "/" },
      { name: "Service Areas", path: "/#service-areas" },
      { name: c.name, path: `/public-adjuster-${c.slug}` },
    ]),
  ];

  return (
    <div className="bg-black min-h-screen text-white">
      <SEO
        title={c.metaTitle}
        description={c.metaDescription}
        path={`/public-adjuster-${c.slug}`}
        keywords={c.keywords}
        image={`https://claimbeast.com${images.roof}`}
        schemas={schemas}
      />
      <Header />
      <Breadcrumbs
        items={[
          { name: "Service Areas", path: "/#service-areas" },
          { name: c.name, path: `/public-adjuster-${c.slug}` },
        ]}
      />

      {/* HERO */}
      <section className="relative bg-black overflow-hidden" data-testid="flagship-hero">
        <div className="absolute inset-0">
          <img
            src={images.roof}
            alt={`Best public adjuster in ${c.name}, FL — Claim Beast`}
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/90 to-black/40" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-16 md:py-24">
          <div className="max-w-3xl">
            <div className="flex items-center gap-2 mb-5">
              <span className="inline-flex items-center gap-1.5 bg-[#E11D26]/15 border border-[#E11D26]/50 text-[#E11D26] px-3 py-1.5 text-xs font-bold tracking-[0.2em] uppercase">
                <MapPin className="h-3.5 w-3.5" /> {c.name}, Florida
              </span>
              <span className="inline-flex items-center gap-1 text-white/70 text-xs font-bold tracking-wider uppercase">
                <Star className="h-3.5 w-3.5 fill-[#E11D26] text-[#E11D26]" /> 5.0 Rated
              </span>
            </div>
            <h1
              className="text-white text-4xl sm:text-5xl md:text-6xl font-black uppercase leading-[0.95]"
              style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
            >
              The Best Public Adjuster in{" "}
              <span className="text-[#E11D26]">{c.name}, FL</span>
            </h1>
            <p className="mt-6 text-white/85 text-base sm:text-lg leading-relaxed max-w-2xl">
              {c.heroIntro}
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link
                to="/#contact"
                data-testid="flagship-cta-review"
                className="group inline-flex items-center justify-center gap-3 bg-[#E11D26] hover:bg-[#c11820] text-white font-bold uppercase tracking-wider px-7 py-4 transition-colors"
              >
                <span className="text-sm">Get My Free Claim Review</span>
                <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
              </Link>
              <a
                href={`tel:${businessInfo.phoneRaw}`}
                className="inline-flex items-center justify-center gap-2 bg-white text-black hover:bg-white/90 font-bold uppercase tracking-wider px-7 py-4 transition-colors"
              >
                <Phone className="h-4 w-4" />
                <span className="text-sm">{businessInfo.phone}</span>
              </a>
            </div>
            <div className="mt-10 grid grid-cols-3 gap-6 max-w-lg border-t border-white/10 pt-8">
              <StatCard value="5.0★" label="Client Rating" />
              <StatCard value="$0" label="Upfront Cost" />
              <StatCard value="24–48h" label="On-Site Response" />
            </div>
          </div>
        </div>
      </section>

      {/* AUTHORITY INTRO */}
      <section className="py-16 md:py-20 bg-[#0A0A0A]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 space-y-5 text-white/80 leading-relaxed text-base sm:text-lg">
          <H2>Why {c.name} Homeowners Trust Claim Beast</H2>
          {c.authorityParas.map((p, i) => (
            <p key={i}>
              {(Array.isArray(p) ? p : [p]).map((seg, j) =>
                typeof seg === "string" ? (
                  <React.Fragment key={j}>{seg}</React.Fragment>
                ) : (
                  <Link
                    key={j}
                    to={seg.to}
                    className="text-[#E11D26] hover:text-white underline underline-offset-2 font-semibold transition-colors"
                  >
                    {seg.label}
                  </Link>
                )
              )}
            </p>
          ))}
        </div>
      </section>

      {/* WHY CARRIERS UNDERPAY */}
      <section className="py-16 md:py-20 bg-black border-y border-white/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <H2>How Insurers Underpay {c.name} Claims</H2>
          <p className="mt-5 text-white/75 leading-relaxed">
            Underpayment is rarely an accident. {c.region} carriers use a
            repeatable playbook to keep their initial number low. Knowing the
            tactics is the first step to defeating them:
          </p>
          <ul className="mt-8 grid sm:grid-cols-2 gap-3">
            {[
              "Paying Actual Cash Value (ACV) when your policy owes Replacement Cost (RCV)",
              "Fast inspections that miss subfloor, decking, and hidden water damage",
              "Labeling storm damage 'wear and tear' or 'cosmetic only'",
              "Skipping Florida code-upgrade coverage (secondary water barrier, straps)",
              "Removing overhead and profit (O&P) on multi-trade repairs",
              `Below-market Xactimate line-item pricing for ${c.name} labor`,
              "Partial-slope roof repairs instead of full, matching replacements",
              "Lowball contents valuation on damaged personal property",
            ].map((item, i) => (
              <li
                key={i}
                className="flex items-start gap-3 bg-[#0F0F0F] border border-white/10 p-4"
              >
                <TrendingUp className="h-5 w-5 text-[#E11D26] shrink-0 mt-0.5" />
                <span className="text-white/85 text-sm leading-relaxed">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* PROCESS */}
      <section className="py-16 md:py-20 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <H2>How We Win Your {c.name} Claim</H2>
          <p className="mt-5 text-white/75 max-w-3xl">
            Every Claim Beast case follows a disciplined, evidence-first process
            designed to make the carrier pay what your policy actually owes.
          </p>
          <div className="mt-10 grid md:grid-cols-4 gap-px bg-white/10 border border-white/10">
            {[
              { n: "01", t: "Free Review", d: "We read your policy and any carrier estimate or denial letter, then tell you exactly where you stand — at no cost." },
              { n: "02", t: "Document", d: "We inspect and document every loss with photos, moisture mapping, drone imagery, and NOAA storm data." },
              { n: "03", t: "Estimate", d: `We build an independent Xactimate estimate at fair ${c.name} pricing — with code upgrades, O&P, and full scope.` },
              { n: "04", t: "Negotiate", d: "We file and negotiate the claim and escalate to appraisal or mediation when the carrier won't pay fairly." },
            ].map((s) => (
              <div key={s.n} className="bg-[#0F0F0F] p-6 sm:p-7">
                <div
                  className="text-[#E11D26] text-4xl font-black leading-none"
                  style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
                >
                  {s.n}
                </div>
                <h3
                  className="mt-4 text-white font-black uppercase text-base tracking-wide"
                  style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
                >
                  {s.t}
                </h3>
                <p className="mt-3 text-white/70 text-sm leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Calculator funnel */}
      <CalculatorCallout
        eyebrow={`${c.name} Claim · 60-Second Estimate`}
        headline={`See what your ${c.name} claim is really worth.`}
        body={`Before you accept the carrier's number, run your damage through our free calculator. It uses current ${c.region} market pricing to return a low–high recovery range in about a minute.`}
        ctaLabel="Estimate My Claim"
        source={`flagship_${c.slug}_callout`}
      />

      {/* CASE STUDIES */}
      <section className="py-16 md:py-20 bg-black border-y border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-[#E11D26] text-xs font-bold tracking-[0.3em] uppercase mb-3">
            Representative Results
          </div>
          <H2>{c.name} Claim Case Studies</H2>
          <p className="mt-4 text-white/60 text-sm max-w-3xl italic">
            The following are representative examples that illustrate the kinds
            of outcomes proper documentation and negotiation can produce. Every
            claim is different; results depend on your policy, damage, and
            evidence, and are not guaranteed.
          </p>
          <div className="mt-10 grid md:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {c.caseStudies.map((cs, i) => (
              <div key={i} className="bg-[#0F0F0F] p-7" data-testid={`flagship-case-${i}`}>
                <div className="flex items-center justify-between">
                  <span className="text-[#E11D26] text-xs font-bold tracking-[0.2em] uppercase">
                    {cs.tag}
                  </span>
                  <span className="text-white/50 text-xs font-bold uppercase tracking-wider">
                    {cs.area}
                  </span>
                </div>
                <div className="mt-5 flex items-end gap-3">
                  <div>
                    <div className="text-white/50 text-[11px] uppercase tracking-wider font-bold">
                      Carrier Offer
                    </div>
                    <div className="text-white/70 text-lg font-black line-through decoration-[#E11D26]/60">
                      {cs.before}
                    </div>
                  </div>
                  <ArrowRight className="h-5 w-5 text-[#E11D26] mb-1.5" />
                  <div>
                    <div className="text-white/50 text-[11px] uppercase tracking-wider font-bold">
                      Recovered
                    </div>
                    <div
                      className="text-[#E11D26] text-2xl font-black leading-none"
                      style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
                    >
                      {cs.after}
                    </div>
                  </div>
                </div>
                <p className="mt-5 text-white/70 text-sm leading-relaxed">{cs.summary}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CLAIM TYPES NARRATIVE */}
      <section className="py-16 md:py-20 bg-black border-b border-white/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 space-y-5 text-white/80 leading-relaxed text-base sm:text-lg">
          <H2>The {c.name} Claims We Handle Every Week</H2>
          <p>
            No two {c.name} losses are the same, but the patterns repeat. Here's
            how the most common claim types play out across {c.region} — and why
            each one so often ends up underpaid without a public adjuster in
            your corner.
          </p>
          <h3
            className="text-white text-lg sm:text-xl font-black uppercase pt-2"
            style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
          >
            Roof Damage Claims in {c.name}
          </h3>
          <p>
            Florida's mix of intense sun, wind, and hail is brutal on roofs.
            Carriers love to approve a partial-slope repair when the whole roof
            actually needs replacing, or they quietly leave off the Florida
            code upgrades — secondary water barriers and hurricane strapping —
            that {c.county} now requires. We prove the full scope with
            test-square inspections and photo evidence, then hold the carrier
            to a complete, code-compliant replacement.
          </p>
          <h3
            className="text-white text-lg sm:text-xl font-black uppercase pt-2"
            style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
          >
            Hurricane &amp; Wind Claims in {c.name}
          </h3>
          <p>
            When a named storm rolls through {c.region}, insurers process
            thousands of claims fast — and fast usually means underpaid. Wind
            uplift, water intrusion behind soffits, and damaged openings get
            missed or denied as pre-existing. We pair NOAA storm data for your
            exact date and ZIP with a detailed field inspection so there's no
            room for the carrier to argue the damage isn't storm-related.
          </p>
          <h3
            className="text-white text-lg sm:text-xl font-black uppercase pt-2"
            style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
          >
            Water Damage Claims in {c.name}
          </h3>
          <p>
            A burst supply line or failed water heater is a sudden, covered
            event under most {c.name} policies — but carriers reflexively reach
            for the "constant or repeated seepage" and "wear and tear"
            exclusions to deny or shrink the payout. Moisture mapping, a
            plumber's report, and documentation of hidden subfloor and cabinet
            damage defeat those defenses and capture the mold remediation
            carriers routinely leave off.
          </p>
          <h3
            className="text-white text-lg sm:text-xl font-black uppercase pt-2"
            style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
          >
            Hail &amp; Fire Claims in {c.name}
          </h3>
          <p>
            Hail damage gets dismissed as "cosmetic" and fire claims get bogged
            down in contents disputes and smoke-damage arguments. Both require
            an adjuster who documents aggressively and values your property and
            belongings at true replacement cost — not the carrier's discounted
            number.
          </p>
          <h3
            className="text-white text-lg sm:text-xl font-black uppercase pt-2"
            style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
          >
            Denied &amp; Underpaid Claims in {c.name}
          </h3>
          <p>
            Already got a denial letter or a check that doesn't come close?
            That's the heart of what we do. Most denials rely on beatable
            reasons, and Florida law lets you reopen and supplement claims
            within the statutory windows. We rebuild the file, submit a formal
            dispute, and escalate to appraisal or mediation when needed.
          </p>
        </div>
      </section>

      {/* COMMON MISTAKES */}
      <section className="py-16 md:py-20 bg-[#0A0A0A] border-b border-white/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 space-y-5 text-white/80 leading-relaxed text-base sm:text-lg">
          <H2>Mistakes {c.name} Homeowners Make After A Loss</H2>
          <p>
            The days right after damage occurs are the most important — and the
            easiest to get wrong. A few avoidable missteps can cost you tens of
            thousands of dollars. Here's what we see most often, and what to do
            instead.
          </p>
          <p>
            <span className="text-white font-bold">Accepting the first check.</span>{" "}
            The carrier's initial offer is a starting position, not a fair
            settlement. Cashing a check that comes with a release can waive your
            right to more. Before you sign or deposit anything, get the number
            reviewed.
          </p>
          <p>
            <span className="text-white font-bold">Starting repairs too soon.</span>{" "}
            Ripping out damaged drywall, roofing, or flooring before it's
            documented destroys the evidence that proves your claim. Mitigate to
            prevent further damage — stop the leak, tarp the roof — but preserve
            everything else and photograph it thoroughly first.
          </p>
          <p>
            <span className="text-white font-bold">Arguing scope on the phone.</span>{" "}
            Everything you say to the carrier can be logged and used to minimize
            your claim. Disputes belong in writing, supported by an independent
            estimate — not in an off-the-cuff phone call with the insurer's
            adjuster.
          </p>
          <p>
            <span className="text-white font-bold">Missing the deadline.</span>{" "}
            Florida's filing windows are strict. Under Fla. Stat. § 627.70132
            you generally have one year to file a new hurricane claim and 18
            months to supplement. Wait too long and even a valid claim can be
            permanently barred. When in doubt, get a free review now rather than
            later.
          </p>
        </div>
      </section>

      {/* FLORIDA STATUTES */}
      <section className="py-16 md:py-20 bg-[#0A0A0A]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="flex items-center gap-3 mb-3">
            <Gavel className="h-5 w-5 text-[#E11D26]" />
            <span className="text-[#E11D26] text-xs font-bold tracking-[0.3em] uppercase">
              Know Your Rights
            </span>
          </div>
          <H2>The Florida Statutes That Protect You</H2>
          <p className="mt-5 text-white/75 leading-relaxed">
            Florida law gives {c.name} homeowners real leverage against
            insurance companies. These are the statutes we use to hold carriers
            accountable:
          </p>
          <div className="mt-9 space-y-px bg-white/10 border border-white/10">
            {statutes.map((s, i) => (
              <div key={i} className="bg-[#0F0F0F] p-6" data-testid={`flagship-statute-${i}`}>
                <div className="flex items-center gap-3 flex-wrap">
                  <Scale className="h-4 w-4 text-[#E11D26] shrink-0" />
                  <span className="text-[#E11D26] font-black text-sm tracking-wide">
                    {s.ref}
                  </span>
                  <span className="text-white font-bold uppercase text-sm tracking-wide">
                    — {s.title}
                  </span>
                </div>
                <p className="mt-3 text-white/70 text-sm leading-relaxed pl-7">{s.text}</p>
              </div>
            ))}
          </div>
          <p className="mt-6 text-white/50 text-xs italic">
            This information is general and educational, not legal advice.
            Statutory deadlines and provisions change; consult the current
            Florida Statutes or an attorney for your specific situation.
          </p>
        </div>
      </section>

      {/* NEIGHBORHOODS */}
      <section className="py-16 md:py-20 bg-black border-y border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center gap-3 mb-3">
            <MapPin className="h-5 w-5 text-[#E11D26]" />
            <span className="text-[#E11D26] text-xs font-bold tracking-[0.3em] uppercase">
              Serving All Of {c.name}
            </span>
          </div>
          <H2>Every {c.name} Neighborhood</H2>
          <p className="mt-5 text-white/75 max-w-3xl">
            We handle claims in every corner of {c.name} and the surrounding{" "}
            {c.region} area — and typically reach your property for an
            inspection within 24–48 hours.
          </p>
          <div className="mt-8 flex flex-wrap gap-2">
            {c.neighborhoods.map((n) => (
              <span
                key={n}
                className="bg-[#0F0F0F] border border-white/10 text-white/80 px-3 py-1.5 text-xs font-bold uppercase tracking-wider"
              >
                {n}
              </span>
            ))}
          </div>
          {c.relatedArea && (
            <Link
              to={c.relatedArea.href}
              data-testid="flagship-related-area"
              className="group mt-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-[#0F0F0F] border border-[#E11D26]/40 hover:border-[#E11D26] hover:bg-[#1a0709] p-6 transition-colors"
            >
              <div className="flex items-start gap-4">
                <MapPin className="h-6 w-6 text-[#E11D26] shrink-0 mt-1" />
                <div>
                  <div className="text-[#E11D26] text-xs font-bold tracking-[0.25em] uppercase">
                    Neighboring Area
                  </div>
                  <p className="mt-1 text-white/75 text-sm leading-relaxed max-w-2xl">
                    {c.relatedArea.note}
                  </p>
                </div>
              </div>
              <span className="inline-flex items-center gap-2 text-white font-bold tracking-wider uppercase text-sm shrink-0">
                {c.relatedArea.name} Page
                <ArrowRight className="h-5 w-5 text-[#E11D26] transition-transform group-hover:translate-x-1" />
              </span>
            </Link>
          )}
        </div>
      </section>

      {/* WHY US */}
      <section className="py-16 md:py-20 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <H2>What Makes Claim Beast Different</H2>
          <div className="mt-10 grid md:grid-cols-3 gap-6">
            {[
              { icon: Shield, t: "We Work For You", d: "Licensed to represent policyholders — never the insurance company. Your payout is our only objective." },
              { icon: FileText, t: "Evidence-First", d: "Xactimate estimates, drone and moisture documentation, and NOAA storm data that carriers can't wave away." },
              { icon: Clock, t: "Local & Fast", d: `${c.region}–based with 24–48 hour on-site response — not a statewide call center reading a script.` },
              { icon: Scale, t: "Florida Law On Your Side", d: "We invoke the Homeowner Claims Bill of Rights, statutory deadlines, mediation, and appraisal to force fair payment." },
              { icon: TrendingUp, t: "No Upfront Cost", d: "Fees are capped by Florida law and only paid out of what we recover. If we don't recover, you don't pay us." },
              { icon: Star, t: "5.0-Star Reputation", d: "A perfect client rating built on transparent communication and results that speak for themselves." },
            ].map((f, i) => (
              <div key={i} className="bg-[#0F0F0F] border border-white/10 p-7">
                <f.icon className="h-7 w-7 text-[#E11D26]" />
                <h3
                  className="mt-4 text-white font-black uppercase text-lg tracking-wide"
                  style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
                >
                  {f.t}
                </h3>
                <p className="mt-3 text-white/70 text-sm leading-relaxed">{f.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {c.extraSections && <ContentSections sections={c.extraSections} />}

      {/* FAQ */}
      <section className="py-16 md:py-20 bg-black border-y border-white/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="text-[#E11D26] text-xs font-bold tracking-[0.3em] uppercase mb-3">
            {c.name} Public Adjuster FAQ
          </div>
          <H2>Frequently Asked Questions</H2>
          <div className="mt-9 divide-y divide-white/10 border-y border-white/10">
            {faqs.map((f, i) => (
              <details key={i} className="group py-5" data-testid={`flagship-faq-${i}`}>
                <summary className="flex justify-between items-start cursor-pointer list-none gap-6">
                  <h3 className="text-white text-base sm:text-lg font-bold uppercase tracking-wide leading-snug">
                    {f.question}
                  </h3>
                  <span className="text-[#E11D26] text-xl group-open:rotate-45 transition-transform shrink-0">
                    +
                  </span>
                </summary>
                <p className="mt-4 text-white/75 leading-relaxed text-sm sm:text-base">
                  {f.answer}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Related claim help */}
      <section className="py-14 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <H2>{c.name} Claim Help By Type</H2>
          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {[
              { t: "Denied Insurance Claim", href: "/denied-insurance-claim-florida" },
              { t: "Underpaid Insurance Claim", href: "/underpaid-insurance-claim-florida" },
              { t: "Roof Claim Denial", href: "/roof-claim-denial-florida" },
              { t: "Hail Damage Claims", href: "/hail-damage" },
              { t: "Water Damage Claims", href: "/water-damage" },
              { t: "Wind / Hurricane Damage", href: "/wind-damage" },
              { t: "Roof Claim Calculator", href: "/roof-claim-calculator" },
              { t: "Water Damage Calculator", href: "/water-damage-claim-calculator" },
              { t: "Free Claim Calculator", href: "/claim-calculator" },
            ].map((l) => (
              <Link
                key={l.href}
                to={l.href}
                className="group flex items-center justify-between gap-4 bg-[#0F0F0F] border border-white/10 hover:border-[#E11D26] hover:bg-[#1a0709] p-5 transition-colors"
              >
                <span className="text-white font-bold text-sm uppercase tracking-wide">
                  {l.t}
                </span>
                <ArrowRight className="h-4 w-4 text-[#E11D26] transition-transform group-hover:translate-x-1" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-14 bg-[#E11D26]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h3
              className="text-white text-2xl sm:text-3xl md:text-4xl font-black uppercase leading-tight"
              style={{ fontFamily: "'Anton', 'Oswald', sans-serif" }}
            >
              Don't settle for the carrier's first offer.
            </h3>
            <p className="mt-2 text-white/90 text-sm sm:text-base">
              Get a free, no-obligation {c.name} claim review today.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 shrink-0">
            <Link
              to="/#contact"
              data-testid="flagship-cta-final"
              className="inline-flex items-center gap-2 bg-black hover:bg-[#0A0A0A] text-white font-bold uppercase tracking-wider px-7 py-4 transition-colors"
            >
              <Calculator className="h-4 w-4" /> Free Claim Review
            </Link>
            <a
              href={`tel:${businessInfo.phoneRaw}`}
              className="inline-flex items-center justify-center gap-2 bg-white text-[#E11D26] hover:bg-white/90 font-bold uppercase tracking-wider px-7 py-4 transition-colors"
            >
              <Phone className="h-4 w-4" /> {businessInfo.phone}
            </a>
          </div>
        </div>
      </section>

      <ReviewedBy />

      <RelatedClaimHelp topic="city" currentPath={`/public-adjuster-${c.slug}`} />

      <Footer />
    </div>
  );
};

export default FlagshipCityPage;
